// Import Core
import React, { useState, useRef, useContext } from "react";
import { Link } from "gatsby";
// Import Libraries
import { GatsbyImage } from "gatsby-plugin-image";
import styled from "styled-components";
import { getDistance } from "geolib";
// Import Components
import SectionTitle from "../../ui/SectionTitle";
import { Button } from "../../ui/Button";
import Pagination from "../../ui/Pagination";
// Import Utils
import { getSlug } from "../../../utils/getSlug";
import { GetLinks } from "../../../utils/getNavItemLinks";
import { getMiles } from "../../../utils/convertDistance";
// Import Logic
import { getHotelsOrPagesFromIds } from "../../../utils/logic/getHotelsOrPagesFromIds";
import { getNearbyHotels } from "../../../utils/logic/getNearbyHotels";
import { getLocation } from "../../../utils/getLocation";
// Import Queries (Context)
import RootContext from "../../../context/RootContext";
import { getNearbyAttractions } from "../../../utils/logic/getNearbyAttractions";
import { getHotelsBySearchTags } from "../../../utils/logic/getHotelsBySearchTags";

/**
 * This page displays 'additional' cards on a pillar page / homepage, e.g., any blocks that are added via page content that will show multiple hotels / pages
 *
 * These cards all have pagination
 */

/* Styled Components */
const AdditionalCardsStyle = styled.section`
  width: 100%;
  .additional-cards_wrapper {
    padding: 40px 120px 64px;

    @media (max-width: 992px) {
      padding: 40px 64px;
    }
    @media (max-width: 768px) {
      padding: 40px 24px;
    }
  }
  .additional-cards {
    --grid-layout-gap: 36px;
    --grid-column-count: 4;
    --grid-item--min-width: 273px;
    @media (max-width: 992px) {
      --grid-layout-gap: 16px;
      --grid-item--min-width: 273px;
    }

    /** Calculated values */
    --gap-count: calc(var(--grid-column-count) - 1);
    --total-gap-width: calc(var(--gap-count) * var(--grid-layout-gap));
    --grid-item--max-width: calc(
      (100% - var(--total-gap-width)) / var(--grid-column-count)
    );

    display: grid;
    grid-template-columns: repeat(
      auto-fill,
      minmax(max(var(--grid-item--min-width), var(--grid-item--max-width)), 1fr)
    );
    grid-gap: var(--grid-layout-gap);

    /* Default - Smaller Cards */
    .card_additional {
      /* box-shadow: none;
			background-color: transparent;
			flex-direction: row; */
      width: 100%;
      /* height: 200px; */
      /* gap: 10px;
			margin: 0 0 64px 0;
			padding: 0; */
      gap: 0;

      @media (max-width: 992px) {
        margin: 0 0 24px 0;
      }
      @media (max-width: 768px) {
        margin: 0 0 16px 0;
      }

      .gatsby-image-wrapper {
        width: 100%;
        height: 200px;
        min-width: 200px;
        min-height: 200px;
      }

      .card_body {
        padding: 16px 24px;
        gap: 16px;
        display: flex;
        align-items: left;
        flex-direction: column;
        justify-content: center;

        .card_link {
          text-decoration: none;
          /* transition: 0.3s all ease-in-out; */
          justify-content: flex-start;
        }
      }
    }

    /* Compact Cards - no border, just title and image */
    .card_additional-compact {
      box-shadow: none;
      background-color: transparent;
      flex-direction: row;
      width: 100%;
      height: 100px;
      gap: 10px;
      margin: 0 0 64px 0;
      padding: 0;

      @media (max-width: 992px) {
        margin: 0 0 64px 0;
      }
      @media (max-width: 768px) {
        margin: 0 0 16px 0;
        &:last-child {
          margin: 0;
        }
      }

      .gatsby-image-wrapper {
        width: 100px;
        height: 100px;
        min-width: 100px;
        min-height: 100px;
      }

      .card_body {
        padding: 0;
        gap: 16px;
        display: flex;
        align-items: left;
        flex-direction: column;
        gap: 8px;
        justify-content: center;

        .card_link {
          text-decoration-color: var(--neutral-900);
          transition: 0.3s all ease-in-out;
        }
      }
    }
  }
`;

// const smoothScrollOnClick = (ref) => {
// 	ref.current?.scrollIntoView({behavior: 'smooth'});
// };

/* Render Component */
const AdditionalCards = ({
  cardsType,
  resultsType,
  allPages,
  sectionTitle,
  pagesToShow,
  className,
  id,
  placeholderImage,
  sectionType,
}) => {
  // Get hotels from graphql query
  // const allHotels = useAllHotels();
  const { allHotels } = useContext(RootContext);

  let allFilteredHotels = [];
  let filteredHotelResults = [];

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = pagesToShow ? pagesToShow : 4;

  // Use Ref to scroll to the top of cards when pagination is used
  const ref = useRef(null);

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  // let currentRecords = allPages.slice(indexOfFirstRecord, indexOfLastRecord);
  // const nPages = Math.ceil(allPages.length / recordsPerPage);
  let subtitle, title;
  let location = getLocation(resultsType);

  /** Render Components */
  const renderNearbyCards = () => {
    if (cardsType === "nearby") {
      // -> if we choose Nearby Hotels
      if (resultsType._type === "hotel") {
        // Filter out current hotel so it doesn't show in results
        let hotels = allPages.filter((htl) => htl._id !== resultsType._id);

        // Filter out hotels that are not in guide or have not paid
        hotels = hotels.filter(
          (hotel) => hotel.displayOptions.inGuide || hotel.displayOptions.paid
        );

        // Filter out hotels that have no coordinates
        hotels = hotels.filter((hotel) => hotel.location && hotel.location.lat);

        // TODO: Section title passed in props - change this!!!
        subtitle = "Features";
        title = "Nearby Hotels";

        // Check if hotel we're on has a location, if not we can't get nearby hotels
        if (location) {
          // All filtered hotel arrays will go in here
          allFilteredHotels = getNearbyHotels(hotels, location);
          allFilteredHotels = getHotelsOrPagesFromIds(
            hotels,
            allFilteredHotels
          );
        }
      } else if (resultsType._type === "pillar") {
        // -> if we choose Nearby Pillar (Editor's Choice)
        title = "Locations";

        // Filter out current hotel so it doesn't show in results
        let allPillars = allPages.filter((htl) => htl._id !== resultsType._id);
        // Check if hotel we're on has a location, if not we can't get nearby allPillars
        if (location && location.lat && location.lng) {
          // All filtered pillar arrays will go in here
          allFilteredHotels = getNearbyHotels(allPillars, location).slice(
            0,
            30
          );

          allFilteredHotels = getHotelsOrPagesFromIds(
            allPillars,
            allFilteredHotels
          );
        }
      }
    } else if (cardsType === "hotelsNearTouristAttractions") {
      // -> if we choose Nearby Tourist Attractions (Pillar Pages)
      title = "Hotels near tourist attractions";

      // All filtered hotel arrays will go in here
      allFilteredHotels = allPages;
      let filteredPillarsIds = getNearbyAttractions(
        allPages,
        resultsType.mapSearchOptions.filterByLocation,
        resultsType.mapSearchOptions.mapSearchRadius
      );
      allFilteredHotels = getHotelsOrPagesFromIds(allPages, filteredPillarsIds);
    }
    // if (sectionType === "nearbyHotels2") {
    //   title = `Other hotels in ${location.value}`;
    // }
    // Limit result to about 30
    filteredHotelResults = allFilteredHotels.slice(0, 30);
    // Calculate number of pages for pagination
    const nPages = Math.ceil(filteredHotelResults.length / recordsPerPage);
    // Update CurrentRecords to split our new array into pages
    const currentRecords = filteredHotelResults.slice(
      indexOfFirstRecord,
      indexOfLastRecord
    );

    // -> get the list of hotels from above array of hotel ids

    return (
      filteredHotelResults.length > 0 && (
        <AdditionalCardsStyle>
          <SectionTitle twoCol subtitle={subtitle} title={title} />
          <div className="additional-cards_wrapper">
            <div className="additional-cards">
              {currentRecords.map((hotel) => {
                let locationToCheck =
                  hotel._type === "hotel"
                    ? hotel.location
                    : hotel.mapSearchOptions.filterByLocation;

                const slug = getSlug(hotel);
                const image =
                  hotel.reviewTextAndMedia &&
                  hotel.reviewTextAndMedia.homepageFeaturedImage &&
                  hotel.reviewTextAndMedia.homepageFeaturedImage.asset
                    ? hotel.reviewTextAndMedia.homepageFeaturedImage
                    : placeholderImage;

                const distanceText =
                  hotel._type === "hotel" && location.lat && location.lng
                    ? `${getMiles(
                        getDistance(location, locationToCheck)
                      )} miles`
                    : null;

                return (
                  <Link
                    to={slug}
                    key={hotel._id}
                    className="card card_additional-compact"
                  >
                    <GatsbyImage
                      image={image.asset.gatsbyImageData}
                      alt={hotel.name}
                    />
                    <div className="card_body">
                      <p className="card_title card_link">{hotel.name}</p>
                      {distanceText && <p>{distanceText}</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
            {nPages > 1 && (
              <Pagination
                nPages={nPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                // smoothScrollOnClick={smoothScrollOnClick}
              />
            )}
          </div>
        </AdditionalCardsStyle>
      )
    );
  };

  const renderSimilarPages = () => {
    // console.log(resultsType);
    let subtitle, title;
    // -> if we choose Nearby Pillar (Editor's Choice)
    // ! FIX: NEED TO PASS IN MAINBLOCKRESULTS & SPECIALOFFERS & remove any hotels from hotels list that feature already on page!
    switch (cardsType) {
      case "similarPages":
        // title = "Other hotels in the group";
        if (resultsType.pillarPageOptions.pageType === "theme") {
          title = `Other ${resultsType?.hotelSearchTags?.filterByTags?.[0]?.label} suggestions`;
        } else {
          if (
            resultsType.hotelSearchOptions.filterByCounty !== "" &&
            resultsType.hotelSearchOptions.filterByCounty !== null
          ) {
            title = `Other suggestions in ${resultsType.hotelSearchOptions.filterByCounty}`;
          } else if (
            resultsType.hotelSearchOptions.filterByTown !== "" &&
            resultsType.hotelSearchOptions.filterByTown !== null
          ) {
            title = `Other suggestions in ${resultsType.hotelSearchOptions.filterByTown}`;
          } else if (
            resultsType.hotelSearchOptions.filterByCountry !== null &&
            resultsType.hotelSearchOptions.filterByCountry.length > 0
          ) {
            title = `Other suggestions in ${resultsType.hotelSearchOptions.filterByCountry[0]}`;
          }
        }

        break;
      case "parentChildPages":
        title = "Similar Hotels";
        break;
      case "similarPagesByLocation":
        if (
          resultsType.hotelSearchOptions.filterByCounty !== "" &&
          resultsType.hotelSearchOptions.filterByCounty !== null
        ) {
          title = `Other suggestions in ${resultsType.hotelSearchOptions.filterByCounty}`;
        } else if (
          resultsType.hotelSearchOptions.filterByTown !== "" &&
          resultsType.hotelSearchOptions.filterByTown !== null
        ) {
          title = `Other suggestions in ${resultsType.hotelSearchOptions.filterByTown}`;
        } else if (
          resultsType.hotelSearchOptions.filterByCountry !== null &&
          resultsType.hotelSearchOptions.filterByCountry.length > 0
        ) {
          title = `Other suggestions in ${resultsType.hotelSearchOptions.filterByCountry[0]}`;
        }
        break;
    }
    if (allPages) {
      // Check and remove current page if it is in the list
      allPages = allPages.filter((page) => page._id !== resultsType.id);
      // Get pagesId from searchTags and get pages by Id
      allPages = getHotelsOrPagesFromIds(allPages,getHotelsBySearchTags(allPages,resultsType))
      // Limit result to about 30
      allPages = allPages.slice(0, 30);
      // Calculate number of pages for pagination
      const nPages = Math.ceil(allPages.length / recordsPerPage);
      // Update CurrentRecords to split our new array into pages
      const currentRecords = allPages.slice(
        indexOfFirstRecord,
        indexOfLastRecord
      );
      return (
        <AdditionalCardsStyle>
          <SectionTitle twoCol subtitle={subtitle} title={title} />
          <div className="additional-cards_wrapper">
            <div className="additional-cards">
              {currentRecords.map((page) => {
                const slug = getSlug(page);

                const image =
                  page.pillarHero &&
                  page.pillarHero.heroImage &&
                  page.pillarHero.heroImage.asset
                    ? page.pillarHero.heroImage
                    : placeholderImage;
                return (
                  <Link
                    to={slug}
                    key={page._id}
                    className="card card_additional-compact"
                  >
                    <GatsbyImage
                      image={image.asset.gatsbyImageData}
                      alt={page.name}
                    />
                    <div className="card_body">
                      <p className="card_title card_link">{page.name}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
            {nPages > 1 && (
              <Pagination
                nPages={nPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                // smoothScrollOnClick={smoothScrollOnClick}
              />
            )}
          </div>
        </AdditionalCardsStyle>
      );
    } else return null;
  };

  // const renderExtendedCards = (cardsType, resultsType, allPages, sectionTitle,  allFilteredHotels, filteredHotelResults, allHotels) => {
  // 	let subtitle, title
  // 				// if(resultsType._type === 'pillar') {

  // 			// AAAAAAAAAAAAH - THIS CAN BE CHANGED TO EXTENDED

  // 				console.log('🍎 🍎 🍎 🍎 🍎 🍎 🍎 🍎 🍎 🍎 🍎 ', {cardsType, resultsType, allPages})
  // 				title = 'Locations - Tourist'
  // 				allFilteredHotels = getNearbyAttractions(allPages, location);
  // 				console.log("🚀 ~ file: AdditionalCards.js ~ line 129 ~ AdditionalCards ~ allFilteredHotels", location, allPages);

  // 		// Limit result to about 10
  // 		allFilteredHotels = allFilteredHotels.slice(0, 10);
  // 		console.log("🚀 ~ file: AdditionalCards.js ~ line 111 ~ AdditionalCards ~ allFilteredHotels", allFilteredHotels);

  // 		// Similar Pages - by first search tag
  // 		// Similar Hotels - by all matching search tags
  // 		// Pages Nearby - from coordinates
  // 		// Hotels Nearby - from coordinates
  // 		// Parent / Child -Pages - for pages only

  // 		// todo add pagination

  // 		// -> get the list of hotels from above array of hotel ids

  // 		const filteredHotelResults = getHotelsOrPagesFromIds(allPages, allFilteredHotels);
  // 		console.log({ filteredHotelResults });
  // 		// ! FIX: DISPLAY DISTANCE FROM PAGE
  // 		return (
  // 			filteredHotelResults.length > 0 &&
  // 			<AdditionalCardsStyle>
  // 				<SectionTitle twoCol subtitle={subtitle} title={title} />
  // 				<div className='additional-cards'>
  // 					{filteredHotelResults.map((attraction) => {
  // 						console.log('AdditionalCards', attraction)
  // 						let locationToCheck = attraction.location

  // 						const slug = attraction.externalLink
  // 						const image =
  // 							attraction.featuredImg &&
  // 							attraction.featuredImg.asset
  // 								? attraction.featuredImg
  // 								: allPlaceholderImages.attractionsPlaceholder;

  // 						return (
  // 							<a href={slug} target='_blank' rel='noreferrer' className='card card_additional-compact'>
  // 								<Img fluid={image.asset.fluid} alt='{attraction.name}' />
  // 								<div className='card_body'>
  // 									<p className='card_title card_link'>{attraction.name}</p>
  // 									<p>{`${getMiles(getDistance(location, locationToCheck))} miles`}</p>
  // 								</div>
  // 							</a>
  // 						);
  // 					})}
  // 				</div>
  // 			</AdditionalCardsStyle>
  // 		);
  // }

  const renderEditorsChoiceCards = () => {
    let title, subtitle;
    title =
      resultsType.pillarPageOptions.pageType === "editorsChoice"
        ? "More Hotels Ideas"
        : "We Recommend";

    if (allPages) {
      // Check and remove current page if it is in the list
      allPages = allPages.filter((page) => page._id !== resultsType._id);
      // Limit result to about 20
      allPages = allPages.slice(0, 20);
      // Calculate number of pages for pagination
      const nPages = Math.ceil(allPages.length / recordsPerPage);
      // Update CurrentRecords to split our new array into pages
      const currentRecords = allPages.slice(
        indexOfFirstRecord,
        indexOfLastRecord
      );
      return (
        <AdditionalCardsStyle>
          <SectionTitle twoCol subtitle={subtitle} title={title} />
          <div className="additional-cards_wrapper">
            <div className="additional-cards">
              {currentRecords.map((page) => {
                const slug = getSlug(page);

                const image =
                  page.pillarHero &&
                  page.pillarHero.heroImage &&
                  page.pillarHero.heroImage.asset
                    ? page.pillarHero.heroImage
                    : placeholderImage;
                return (
                  <Link
                    to={slug}
                    key={page._id}
                    className="card card_additional-compact"
                  >
                    <GatsbyImage
                      image={image.asset.gatsbyImageData}
                      alt={page.name}
                    />
                    <div className="card_body">
                      <p className="card_title card_link">{page.name}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
            {nPages > 1 && (
              <Pagination
                nPages={nPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                // smoothScrollOnClick={smoothScrollOnClick}
              />
            )}
          </div>
        </AdditionalCardsStyle>
      );
    } else return null;
  };

  const renderCollectionCards = () => {
    // console.log(allPages); 
    if (allPages) {
      // Check and remove current page if it is in the list
      allPages = allPages.filter((page) => page._id !== resultsType._id);
      // Limit result to about 30
      allPages = allPages.slice(0, 20);
      // Calculate number of pages for pagination
      const nPages = Math.ceil(allPages.length / recordsPerPage);
      // Update CurrentRecords to split our new array into pages
      const currentRecords = allPages.slice(
        indexOfFirstRecord,
        indexOfLastRecord
      );

      return (
        <AdditionalCardsStyle ref={ref}>
          <div className={className ? className : ""} id={id ? id : ""}></div>
          <SectionTitle
            // twoCol={sectionTitle.twoCol}
            twoCol
            subtitle={sectionTitle.subtitle}
            title={sectionTitle.title}
            content={sectionTitle.caption}
            className={sectionTitle.className}
            // FIX: NEEDS TO COME IN FROM ORIGINAL COMPONENT
            button={
              sectionTitle.navItem &&
              sectionTitle.navItem.name && (
                <GetLinks navItem={sectionTitle.navItem} />
              )
            }

            // button={sectionTitle.button}
          />
          <div className="additional-cards_wrapper">
            <div className="additional-cards">
              {currentRecords.map((page) => {
                const slug = getSlug(page);
                let image;
                if (page._type === "hotel") {
                  image =
                    page.reviewTextAndMedia.homepageFeaturedImage &&
                    page.reviewTextAndMedia.homepageFeaturedImage.asset
                      ? page.reviewTextAndMedia.homepageFeaturedImage
                      : placeholderImage;
                } else if (page._type === "pillar") {
                  image =
                    page.pillarHero &&
                    page.pillarHero.heroImage &&
                    page.pillarHero.heroImage.asset
                      ? page.pillarHero.heroImage
                      : placeholderImage;
                } else {
                  image = page.featuredImage
                    ? page.featuredImage
                    : placeholderImage;
                }

                return (
                  <Link
                    to={slug}
                    key={page._id}
                    className={
                      cardsType === "collectionCompact"
                        ? "card card_additional-compact"
                        : "card card_additional"
                    }
                  >
                    <GatsbyImage
                      image={image.asset.gatsbyImageData}
                      alt={page.name}
                    />

                    {cardsType === "collectionCompact" && (
                      <div className="card_body">
                        <p className="card_title card_link">{page.name}</p>
                      </div>
                    )}
                    {cardsType === "collectionDefault" && (
                      <div className="card_body">
                        <h4 className="card_title">{page.name}</h4>
                        <Button className="text-dark-btn card_link">
                          Discover
                        </Button>
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
            {nPages > 1 && (
              <Pagination
                nPages={nPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                // smoothScrollOnClick={smoothScrollOnClick}
              />
            )}
          </div>
        </AdditionalCardsStyle>
      );
    } else return null;
  };

  // -> NEARBY
  if (cardsType === "nearby" || cardsType === "hotelsNearTouristAttractions") {
    return renderNearbyCards(
      cardsType,
      resultsType,
      allPages,
      sectionTitle,
      allFilteredHotels,
      filteredHotelResults,
      allHotels,
      placeholderImage
    );
  }

  // -> SIMILAR HOTELS ("TYPES OF HOTEL")
  if (
    cardsType === "similarPages" ||
    cardsType === "parentChildPages" ||
    cardsType === "similarPagesByLocation"
  ) {
    return renderSimilarPages(
      cardsType,
      resultsType,
      allPages,
      sectionTitle,
      allFilteredHotels,
      filteredHotelResults,
      allHotels,
      placeholderImage
    );
  }

  // -> NEARBY EXTENDED
  // ! FOLLOW UP: THIS MIGHT BE NEEDED SO LEAVING IN!!!!!!!!!!!!
  // if(cardsType === 'nearbyExtended') {
  // 	renderExtendedCards(cardsType, resultsType, allPages, sectionTitle,  allFilteredHotels, filteredHotelResults, allHotels)
  // }

  // task FILTER OUT IDS THAT FEATURE - SAVE THEM IN AN ARRAY?
  // -> Choice: editors Choice Pages 'More Hotels Nearby / We Recommend'
  if (cardsType === "editorsChoicePages") {
    return renderEditorsChoiceCards(
      cardsType,
      resultsType,
      allPages,
      sectionTitle,
      allFilteredHotels,
      filteredHotelResults,
      allHotels,
      placeholderImage
    );
  }

  if (cardsType === "collectionDefault" || cardsType === "collectionCompact") {
    // Update CurrentRecords to split our new array into pages
    const currentRecords = allPages.slice(
      indexOfFirstRecord,
      indexOfLastRecord
    );
    return renderCollectionCards(
      cardsType,
      resultsType,
      allPages,
      sectionTitle,
      allFilteredHotels,
      filteredHotelResults,
      allHotels,
      placeholderImage,
      currentPage,
      setCurrentPage,
      recordsPerPage,
      ref,
      indexOfLastRecord,
      indexOfFirstRecord,
      currentRecords
      // nPages
    );
  }
};

export default AdditionalCards;
